import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuthedImage, uploadImage } from "@/lib/files";
import { ArrowUpRight, Circle, Pencil, Undo2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#EF4444", "#F59E0B", "#10B981", "#0EA5E9", "#111827"];

/**
 * Photo annotator. Loads a source image onto a canvas, lets the user draw
 * arrows / circles / freehand on top, then flattens to PNG and uploads it
 * as a new image. Calls onDone({ path }) when the annotated image is saved.
 */
export function AnnotateDialog({ open, onOpenChange, sourcePath, onDone }) {
  const src = useAuthedImage(open ? sourcePath : null);
  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState(COLORS[0]);
  const [shapes, setShapes] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!open) { setShapes([]); setDrawing(null); }
  }, [open]);

  // Load image and set canvas size
  useEffect(() => {
    if (!src || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const c = canvasRef.current;
      if (!c) return;
      const maxW = 800;
      const scale = Math.min(1, maxW / img.naturalWidth);
      c.width = Math.round(img.naturalWidth * scale);
      c.height = Math.round(img.naturalHeight * scale);
      redraw([]);
    };
    img.src = src;
  }, [src]);

  const redraw = (currentShapes) => {
    const c = canvasRef.current;
    const img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    for (const s of currentShapes) drawShape(ctx, s);
    if (drawing) drawShape(ctx, drawing);
  };

  useEffect(() => { redraw(shapes); /* eslint-disable-next-line */ }, [shapes, drawing]);

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (s.type === "arrow") {
      const [start, end] = s.points;
      if (!end) return;
      ctx.beginPath();
      ctx.moveTo(start[0], start[1]);
      ctx.lineTo(end[0], end[1]);
      ctx.stroke();
      // arrowhead
      const angle = Math.atan2(end[1] - start[1], end[0] - start[0]);
      const head = 14;
      ctx.beginPath();
      ctx.moveTo(end[0], end[1]);
      ctx.lineTo(end[0] - head * Math.cos(angle - Math.PI / 7), end[1] - head * Math.sin(angle - Math.PI / 7));
      ctx.lineTo(end[0] - head * Math.cos(angle + Math.PI / 7), end[1] - head * Math.sin(angle + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    } else if (s.type === "circle") {
      const [start, end] = s.points;
      if (!end) return;
      const rx = Math.abs(end[0] - start[0]) / 2;
      const ry = Math.abs(end[1] - start[1]) / 2;
      const cx = (start[0] + end[0]) / 2;
      const cy = (start[1] + end[1]) / 2;
      ctx.beginPath();
      ctx.ellipse(cx, cy, Math.max(rx, 4), Math.max(ry, 4), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.type === "pen") {
      ctx.beginPath();
      s.points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
    }
  };

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return [(clientX - rect.left) * scaleX, (clientY - rect.top) * scaleY];
  };

  const onDown = (e) => {
    e.preventDefault();
    const p = pos(e);
    if (tool === "pen") setDrawing({ type: "pen", color, points: [p] });
    else setDrawing({ type: tool, color, points: [p, p] });
  };
  const onMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = pos(e);
    if (tool === "pen") setDrawing((d) => ({ ...d, points: [...d.points, p] }));
    else setDrawing((d) => ({ ...d, points: [d.points[0], p] }));
  };
  const onUp = () => {
    if (drawing) setShapes((s) => [...s, drawing]);
    setDrawing(null);
  };

  const undo = () => setShapes((s) => s.slice(0, -1));
  const clear = () => setShapes([]);

  const save = async () => {
    const c = canvasRef.current;
    if (!c || !imgRef.current) return;
    setSaving(true);
    try {
      const blob = await new Promise((res) => c.toBlob(res, "image/png"));
      const file = new File([blob], "annotation.png", { type: "image/png" });
      const { path } = await uploadImage(file);
      toast.success("Marked up photo posted");
      onDone?.({ path });
      onOpenChange(false);
    } catch {
      toast.error("Could not save annotation");
    } finally { setSaving(false); }
  };

  const tools = [
    { k: "arrow", i: ArrowUpRight, label: "Arrow" },
    { k: "circle", i: Circle, label: "Circle" },
    { k: "pen", i: Pencil, label: "Pen" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Mark up this photo</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          {tools.map(({ k, i: I, label }) => (
            <button
              key={k}
              onClick={() => setTool(k)}
              data-testid={`annot-tool-${k}`}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border ${tool === k ? "bg-[color:hsl(var(--teal))] text-white border-[color:hsl(var(--teal))]" : "bg-secondary"}`}
            >
              <I className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
          <div className="w-px h-6 bg-border mx-1" />
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              data-testid={`annot-color-${c}`}
              className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-foreground" : "border-transparent"}`}
              style={{ background: c }}
              aria-label={`color ${c}`}
            />
          ))}
          <div className="flex-1" />
          <button onClick={undo} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="annot-undo"><Undo2 className="w-3.5 h-3.5" /> Undo</button>
          <button onClick={clear} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="annot-clear"><Trash2 className="w-3.5 h-3.5" /> Clear</button>
        </div>
        <div className="mt-2 border rounded-xl overflow-hidden bg-secondary/30 grid place-items-center min-h-[300px]">
          {src ? (
            <canvas
              ref={canvasRef}
              onMouseDown={onDown}
              onMouseMove={onMove}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              onTouchStart={onDown}
              onTouchMove={onMove}
              onTouchEnd={onUp}
              className="max-w-full touch-none cursor-crosshair"
              data-testid="annot-canvas"
            />
          ) : (
            <div className="text-muted-foreground text-sm py-10">Loading photo…</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || shapes.length === 0} className="btn-teal" data-testid="annot-save">
            {saving ? "Saving…" : "Post marked-up photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
